import { ShopOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
  message,
} from "antd";
import { useState } from "react";
import { downloadFile } from "../../utils/downloadFile";
import GeneralTableHeader from "../common/general-table-header/GeneralTableHeader";
import {
  BuildSegmentParams,
  BuildSegmentResult,
  EnrichMerchantsResult,
  MarketingCategory,
  PipedriveMatchResult,
  PipedriveStatusFilter,
  SegmentRow,
  buildMarketingSegmentServer,
  enrichMerchantsServer,
  matchPipedriveServer,
} from "./marketingApi";

const { Text, Paragraph } = Typography;

const CATEGORY_OPTIONS: { label: string; value: MarketingCategory }[] = [
  { label: "Bars", value: "bar" },
  { label: "Cafes", value: "cafe" },
  { label: "Restaurants", value: "restaurant" },
];

function toCSV(rows: SegmentRow[]): string {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (val: unknown) => {
    const s = val === undefined || val === null ? "" : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  rows.forEach((row) => {
    lines.push(headers.map((h) => escape((row as any)[h])).join(","));
  });
  return lines.join("\n");
}

export default function MarketingSegments() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [matching, setMatching] = useState(false);
  const [result, setResult] = useState<BuildSegmentResult | null>(null);
  const [enrichResult, setEnrichResult] = useState<EnrichMerchantsResult | null>(
    null
  );
  const [matchResult, setMatchResult] = useState<PipedriveMatchResult | null>(
    null
  );

  const buildParams = (mode: "count" | "rows"): BuildSegmentParams => {
    const values = form.getFieldsValue();

    return {
      categories: values.categories,
      useMccFallback: values.useMccFallback,
      country: values.country || "NL",
      tpvThresholdEur: values.tpvThresholdEur ?? 250,
      lookbackWeeks: values.lookbackWeeks ?? 8,
      minMatchConfidence: values.minMatchConfidence ?? 0.6,
      statuses: values.statuses,
      mode,
      pipedriveStatus: (values.pipedriveStatus ||
        "all") as PipedriveStatusFilter,
      pipedrive: {
        campaign: values.campaign,
      },
    };
  };

  const handlePreview = async () => {
    setLoading(true);
    setResult(null);
    try {
      const data = await buildMarketingSegmentServer(buildParams("count"));
      setResult(data);
      message.success(`Segment contains ${data.count} merchants`);
    } catch (err: any) {
      message.error(err?.message || "Failed to build segment preview");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const data = await buildMarketingSegmentServer(buildParams("rows"));
      setResult(data);

      if (data.mode === "export" && data.url) {
        window.open(data.url, "_blank");
        message.success(
          `Large segment (${data.count} rows) exported to file. Download started.`
        );
        return;
      }

      if (!data.rows || data.rows.length === 0) {
        message.warning("No merchants matched this segment");
        return;
      }

      const csv = toCSV(data.rows);
      const campaign = form.getFieldValue("campaign") || "segment";
      downloadFile(csv, `marketing-${campaign}-${data.count}.csv`, "CSV");
      message.success(`Exported ${data.rows.length} merchants to CSV`);
      if (data.truncated) {
        message.warning(
          "Result was truncated to the row cap. Narrow the filters for the full set."
        );
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to export segment");
    } finally {
      setLoading(false);
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    setEnrichResult(null);
    try {
      const values = form.getFieldsValue();
      const data = await enrichMerchantsServer({
        limit: values.enrichLimit ?? 50,
        country: values.country || "NL",
        minMatchConfidence: values.minMatchConfidence ?? 0.6,
      });
      setEnrichResult(data);
      message.success(
        `Enriched ${data.enriched}/${data.scanned} (review: ${data.needsReview}, no match: ${data.noMatch})`
      );
    } catch (err: any) {
      message.error(err?.message || "Failed to run enrichment");
    } finally {
      setEnriching(false);
    }
  };

  const handleMatchPipedrive = async () => {
    setMatching(true);
    setMatchResult(null);
    try {
      const values = form.getFieldsValue();
      const data = await matchPipedriveServer({
        limit: values.pdMatchLimit ?? 15,
        categories: values.categories,
        country: values.country || "NL",
        statuses: values.statuses,
        minMatchConfidence: values.minMatchConfidence ?? 0.6,
      });
      setMatchResult(data);
      message.success(
        `Matched ${data.matched}/${data.scanned} (ambiguous: ${data.ambiguous}, none: ${data.none})`
      );
    } catch (err: any) {
      message.error(err?.message || "Failed to run Pipedrive match");
    } finally {
      setMatching(false);
    }
  };

  const previewColumns = [
    { title: "Business", dataIndex: "businessName", key: "businessName" },
    { title: "Category", dataIndex: "category", key: "category" },
    { title: "City", dataIndex: "city", key: "city" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    { title: "TPV (EUR)", dataIndex: "tpvEur", key: "tpvEur" },
    {
      title: "Confidence",
      dataIndex: "matchConfidence",
      key: "matchConfidence",
    },
    {
      title: "In Pipedrive",
      dataIndex: "pdMatchStatus",
      key: "pdMatchStatus",
      render: (status: string, row: SegmentRow) => {
        if (status === "matched" && row.pipedriveUrl) {
          return (
            <a href={row.pipedriveUrl} target="_blank" rel="noreferrer">
              matched
            </a>
          );
        }
        return status || "—";
      },
    },
  ];

  return (
    <div>
      <GeneralTableHeader
        title="Marketing Segments"
        statisticsCount={result?.count ?? 0}
        statisticsLabel="Merchants in segment"
        statisticsLoading={loading}
        description="Build, preview and export targeted merchant segments (e.g. inactive NL bars & cafes) for Pipedrive / SendGrid campaigns."
        avatarUrl={<ShopOutlined style={{ fontSize: "30px" }} />}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          categories: ["bar", "cafe"],
          useMccFallback: false,
          country: "NL",
          tpvThresholdEur: 250,
          lookbackWeeks: 8,
          minMatchConfidence: 0.6,
          statuses: ["verified"],
          enrichLimit: 50,
          pdMatchLimit: 15,
          pipedriveStatus: "all",
        }}
      >
        <Card title="Segment filters" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="categories" label="Categories (enriched)">
                <Select
                  mode="multiple"
                  options={CATEGORY_OPTIONS}
                  placeholder="Bars, Cafes, Restaurants"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="country" label="Country">
                <Input placeholder="NL" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="statuses" label="Business status">
                <Select
                  mode="multiple"
                  options={[
                    { label: "verified", value: "verified" },
                    { label: "unverified", value: "unverified" },
                    { label: "pendingVerification", value: "pendingVerification" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="useMccFallback"
                label="MCC fallback"
                valuePropName="checked"
                tooltip="Also include not-yet-enriched merchants matching hospitality MCCs"
              >
                <Checkbox>Include non-enriched MCC matches</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="tpvThresholdEur"
                label="Inactive TPV threshold (EUR)"
                tooltip="Gross volume at or below this over the lookback window counts as inactive"
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="lookbackWeeks" label="Lookback (weeks)">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="minMatchConfidence"
                label="Min match confidence"
                tooltip="0-1; only trust enriched categories at or above this confidence"
              >
                <InputNumber min={0} max={1} step={0.05} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="pipedriveStatus"
                label="Pipedrive status"
                tooltip="Split the export into update rows (already in Pipedrive) vs create rows (new)"
              >
                <Select
                  options={[
                    { label: "All", value: "all" },
                    { label: "Matched (update)", value: "matched" },
                    { label: "Unmatched (create)", value: "unmatched" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Campaign tag" style={{ marginBottom: 16 }}>
          <Paragraph type="secondary">
            Emitted as a <Text code>pipedriveCampaign</Text> column on{" "}
            <Text strong>every</Text> row — import it as a Pipedrive label so the
            whole promo list (both updated matched orgs and newly created
            unmatched ones) becomes one filterable working list / SendGrid
            audience. The label only tags; the update target (org link) stays
            matched-only.
          </Paragraph>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="campaign" label="Campaign label">
                <Input placeholder="world-championship-2026" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Space style={{ marginBottom: 16 }}>
          <Button type="default" onClick={handlePreview} loading={loading}>
            Preview count
          </Button>
          <Button type="primary" onClick={handleExport} loading={loading}>
            Export CSV
          </Button>
        </Space>

        {result && (
          <Card style={{ marginBottom: 16 }}>
            <Space size="large">
              <Statistic title="Matched merchants" value={result.count} />
              {result.truncated && (
                <Text type="warning">Truncated to row cap</Text>
              )}
              {result.mode === "export" && result.url && (
                <a href={result.url} target="_blank" rel="noreferrer">
                  Download file
                </a>
              )}
            </Space>
            {result.rows && result.rows.length > 0 && (
              <Table
                style={{ marginTop: 16 }}
                size="small"
                rowKey={(_, i) => String(i)}
                dataSource={result.rows.slice(0, 50)}
                columns={previewColumns}
                pagination={false}
                scroll={{ y: 360 }}
              />
            )}
          </Card>
        )}

        <Divider />

        <Card title="Enrichment (Google Places)">
          <Paragraph type="secondary">
            Resolve Google Place IDs and granular categories for NL hospitality
            merchants. Runs a batch of merchants per click; repeat to backfill.
          </Paragraph>
          <Space align="baseline">
            <Form.Item name="enrichLimit" label="Batch size">
              <InputNumber min={1} max={500} />
            </Form.Item>
            <Button onClick={handleEnrich} loading={enriching}>
              Run enrichment batch
            </Button>
          </Space>
          {enrichResult && (
            <Space size="large" style={{ marginTop: 8 }}>
              <Statistic title="Scanned" value={enrichResult.scanned} />
              <Statistic title="Enriched" value={enrichResult.enriched} />
              <Statistic title="Matched" value={enrichResult.matched} />
              <Statistic title="Needs review" value={enrichResult.needsReview} />
              <Statistic title="No match" value={enrichResult.noMatch} />
            </Space>
          )}
        </Card>

        <Divider />

        <Card title="Pipedrive matching (read-only)">
          <Paragraph type="secondary">
            Resolve each in-scope merchant to an existing Pipedrive organization
            via phone / email / name signals so the export says update-vs-create.
            Read-only against Pipedrive; idempotent and cached on the merchant.
            Uses the segment filters above (categories, country, status,
            confidence). Run a batch per click; repeat to backfill.
          </Paragraph>
          <Space align="baseline">
            <Form.Item name="pdMatchLimit" label="Batch size">
              <InputNumber min={1} max={100} />
            </Form.Item>
            <Button onClick={handleMatchPipedrive} loading={matching}>
              Run Pipedrive match batch
            </Button>
          </Space>
          {matchResult && (
            <Space size="large" style={{ marginTop: 8 }}>
              <Statistic title="Scanned" value={matchResult.scanned} />
              <Statistic title="Matched" value={matchResult.matched} />
              <Statistic title="Ambiguous" value={matchResult.ambiguous} />
              <Statistic title="None" value={matchResult.none} />
              <Statistic title="Errors" value={matchResult.errors} />
            </Space>
          )}
        </Card>
      </Form>
    </div>
  );
}
